<template>
  <div class="file-uploader">
    <!-- Upload Section -->
    <div class="upload-section bg-white rounded-lg shadow-sm border p-6 mb-6">
      <h3 v-if="showTitle" class="text-lg font-semibold text-gray-800 mb-4">{{ title }}</h3>
      
      <!-- Image Preview Mode -->
      <div v-if="imagePreviewMode && (files.length > 0 || previewImage)" class="image-preview-container mb-4">
        <div class="image-preview-wrapper relative">
          <img 
            :src="previewImage || (files.length > 0 ? files[0].fullUrl || files[0].url : '')" 
            alt="Preview"
            class="w-full h-auto rounded-lg object-cover"
            style="max-height: 300px;"
          />
          <div v-if="uploading" class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-lg">
            <div class="text-center">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-white inline-block mb-2"></div>
              <div class="text-white">Uploading...</div>
        </div>
        </div>
          <button 
            v-if="files.length > 0 && allowDelete && !uploading" 
            @click="deleteFile(files[0])"
            class="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700 transition-colors"
            title="Remove image"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        
        <!-- Image Upload Controls -->
        <div class="mt-3 flex items-center justify-center">
          <button 
            @click="$refs.fileInput.click()"
            class="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            :disabled="uploading"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
            </svg>
            {{ files.length > 0 ? 'Change Image' : 'Select Image' }}
          </button>
        </div>
      </div>
      
      <!-- Standard Upload Area (visible when not in image preview mode or no images uploaded yet) -->
      <div 
        v-if="!imagePreviewMode || (imagePreviewMode && files.length === 0 && !previewImage)"
        @click="$refs.fileInput.click()"
        @drop="handleDrop"
        @dragover.prevent
        @dragenter.prevent
        @dragleave="dragLeaveCounter--"
        @dragenter="dragLeaveCounter++"
        :class="[
          'upload-area border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer',
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        ]"
      >
        <div class="mb-4">
          <svg class="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
          </svg>
        </div>
        <p class="text-gray-600 mb-2">
          <span class="font-medium">Click to upload</span> or drag and drop files here
        </p>
        <p class="text-sm text-gray-500">{{ supportText || 'Support for images, videos, documents and more' }}</p>
        <input 
          ref="fileInput"
          type="file" 
          :multiple="multiple" 
          :accept="accept"
          @change="handleFileSelect"
          class="hidden"
        >
        <button 
          v-if="!autoUpload"
          @click.stop="$refs.fileInput.click()"
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Select Files
        </button>
      </div>
      
      <!-- Upload Queue (only visible when not in image preview mode) -->
      <div v-if="!imagePreviewMode && uploadQueue.length > 0" class="upload-queue mt-6">
        <h4 class="font-medium text-gray-800 mb-3">Upload Queue ({{ uploadQueue.length }} {{ uploadQueue.length === 1 ? 'file' : 'files' }})</h4>
        <div class="space-y-2">
          <div 
            v-for="(upload, index) in uploadQueue" 
            :key="index"
            class="upload-item flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex items-center flex-1">
              <div class="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div class="flex-1">
                <div class="font-medium text-gray-800">{{ upload.customKey || upload.file.name }}</div>
                <div class="text-sm text-gray-600">{{ formatFileSize(upload.file.size) }}</div>
                <div v-if="upload.progress !== undefined" class="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    :style="{ width: upload.progress + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input 
                v-if="allowRename"
                v-model="upload.customKey"
                type="text" 
                :placeholder="upload.file.name"
                class="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                style="min-width: 150px;"
              >
              <button 
                @click="removeFromQueue(index)"
                :disabled="upload.uploading"
                class="text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div class="mt-4 flex gap-2">
          <button 
            @click="uploadFiles"
            :disabled="uploading || uploadQueue.every(u => u.uploading)"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ uploading ? 'Uploading...' : `Upload ${uploadQueue.length} ${uploadQueue.length === 1 ? 'File' : 'Files'}` }}
          </button>
      <button 
            @click="clearQueue"
        :disabled="uploading"
            class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
            Clear Queue
      </button>
        </div>
      </div>
      </div>
      
    <!-- Files List (only shown when not in image preview mode) -->
    <div v-if="!imagePreviewMode && showFilesList && files.length > 0" class="files-list mt-4">
      <h4 class="font-medium text-gray-800 mb-3">Uploaded Files</h4>
      <div class="bg-white rounded-lg shadow-sm border p-4">
        <div class="space-y-2">
          <div 
            v-for="file in files" 
            :key="file.key || file.id"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex items-center flex-1">
              <div class="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div class="flex-1">
                <div class="font-medium text-gray-800">{{ file.name || file.key }}</div>
                <div class="text-sm text-gray-600">{{ formatFileSize(file.size || 0) }}</div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button 
                v-if="allowCopy"
                @click="copyUrl(file.url || file.fullUrl)"
                class="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Copy URL
              </button>
              <button 
                v-if="allowPreview"
                @click="previewFile(file)"
                class="px-2 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Preview
              </button>
              <button 
                v-if="allowDelete"
                @click="deleteFile(file)"
                class="text-red-600 hover:text-red-800"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      
    <!-- Gallery Mode for multiple images -->
    <div v-if="galleryMode && files.length > 0" class="gallery-view mt-4">
      <h4 class="font-medium text-gray-800 mb-3">Uploaded Images</h4>
      <div class="bg-white rounded-lg shadow-sm border p-4">
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          <div 
            v-for="file in files" 
            :key="file.key || file.id"
            class="relative group"
          >
            <div class="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-100">
              <img 
                :src="file.fullUrl || file.url" 
                :alt="file.name || file.key"
                class="w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div class="flex space-x-2">
                  <button 
                    v-if="allowPreview"
                    @click="previewFile(file)"
                    class="p-1.5 bg-gray-800 text-white rounded-full hover:bg-gray-700"
                    title="Preview"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button 
                    v-if="allowCopy"
                    @click="copyUrl(file.url || file.fullUrl)"
                    class="p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700"
                    title="Copy URL"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                    </svg>
                  </button>
                  <button 
                    v-if="allowDelete"
                    @click="deleteFile(file)"
                    class="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700"
                    title="Delete"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="text-sm text-gray-700 mt-1 truncate">{{ file.name || file.key }}</div>
          </div>
          
          <!-- Add Image Button -->
          <div 
            @click="$refs.fileInput.click()"
            class="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <svg class="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            <div class="text-sm text-gray-600">Add Image</div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toast notifications -->
    <div 
      v-if="showToast.show" 
      :class="[
        'fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg transition-opacity z-50',
        showToast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      ]"
    >
      {{ showToast.message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'FileUploader',
  props: {
    // Upload configuration
    apiUrl: {
      type: String,
      default: 'https://r2-eu1.kewaarcdn.com/api/files'
    },
    cdnBaseUrl: {
      type: String,
      default: 'https://eu1.kewaarcdn.com'
    },
    multiple: {
      type: Boolean,
      default: true
    },
    accept: {
      type: String,
      default: '' // e.g. 'image/*,.pdf'
    },
    title: {
      type: String,
      default: 'Upload Files'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    supportText: {
      type: String,
      default: ''
    },
    // Features control
    showFilesList: {
      type: Boolean,
      default: true
    },
    allowRename: {
      type: Boolean,
      default: true
    },
    allowDelete: {
      type: Boolean,
      default: true
    },
    allowPreview: {
      type: Boolean,
      default: true
    },
    allowCopy: {
      type: Boolean,
      default: true
    },
    // Initial files to display
    initialFiles: {
      type: Array,
      default: () => []
    },
    // Auto upload when files are selected
    autoUpload: {
      type: Boolean,
      default: false
    },
    // Display modes
    imagePreviewMode: {
      type: Boolean,
      default: false
    },
    galleryMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Upload functionality
      uploadQueue: [],
      uploading: false,
      isDragging: false,
      dragLeaveCounter: 0,
      
      // Files list
      files: [],
      
      // Preview for image upload
      previewImage: null,
      
      // Toast notifications
      showToast: {
        show: false,
        message: '',
        type: 'success'
      }
    };
  },
  watch: {
    dragLeaveCounter(newValue) {
      this.isDragging = newValue > 0;
    },
    initialFiles: {
      immediate: true,
      handler(newFiles) {
        if (newFiles && newFiles.length) {
          this.files = [...newFiles];
        }
      }
    }
  },
  methods: {
    // File size formatter
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    
    // Upload queue management
    handleDrop(event) {
      event.preventDefault();
      this.dragLeaveCounter = 0;
      this.isDragging = false;
      
      const files = Array.from(event.dataTransfer.files);
      this.addFilesToQueue(files);
      
      // Generate preview for image in image preview mode
      if (this.imagePreviewMode && files.length > 0 && files[0].type.startsWith('image/')) {
        this.generatePreview(files[0]);
      }
      
      if (this.autoUpload && files.length > 0) {
        this.uploadFiles();
      }
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;
      
      this.addFilesToQueue(files);
      
      // Generate preview for image in image preview mode
      if (this.imagePreviewMode && files.length > 0 && files[0].type.startsWith('image/')) {
        this.generatePreview(files[0]);
      }

      // Reset the input
      event.target.value = '';
      
      if (this.autoUpload && files.length > 0) {
        this.uploadFiles();
      }
    },
    
    generatePreview(file) {
      if (!file || !file.type.startsWith('image/')) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewImage = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    addFilesToQueue(files) {
      // In image preview mode, we only keep one file at a time
      if (this.imagePreviewMode && !this.multiple) {
        this.uploadQueue = [];
      }
      
      const newUploads = files.map(file => ({
        file,
        customKey: file.name,
        uploading: false,
        progress: undefined
      }));
      
      this.uploadQueue.push(...newUploads);
      this.$emit('files-added', newUploads);
    },

    removeFromQueue(index) {
      // Clear preview if removing the file that is being previewed
      if (this.imagePreviewMode && index === 0) {
        this.previewImage = null;
      }
      
      this.uploadQueue.splice(index, 1);
      this.$emit('file-removed-from-queue', index);
    },

    clearQueue() {
      this.uploadQueue = [];
      this.previewImage = null;
      this.$emit('queue-cleared');
    },

    async uploadFiles() {
      if (this.uploadQueue.length === 0) return;
      
      this.uploading = true;
      this.$emit('upload-started', this.uploadQueue);
      
      const uploads = [...this.uploadQueue];
      const uploadedFiles = [];
      
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        if (upload.uploading) continue;
        
        try {
          upload.uploading = true;
          upload.progress = 0;
          
          const formData = new FormData();
          formData.append('file', upload.file);
          formData.append('key', upload.customKey || upload.file.name);

          const response = await axios.post(`${this.apiUrl}/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              upload.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          });
          
          // Add uploaded file to files list
          if (response.data) {
            let fileData = {};
            
            console.log('🔥 Upload response received:', JSON.stringify(response.data, null, 2));
            
            // Handle your API response structure
            if (response.data.data && response.data.data.key) {
              // Your API structure: { success: true, data: { key: "...", location: "...", ... } }
              const key = response.data.data.key;
              console.log('🔥 Extracted key from response:', key);
              
              fileData = {
                key: key,
                name: upload.file.name,
                fullUrl: response.data.data.location,
                url: response.data.data.location,
                size: upload.file.size,
                ...response.data.data
              };
              
              console.log('🔥 Constructed fileData object:', JSON.stringify(fileData, null, 2));
            } else if (response.data.url) {
              // Alternative structure
              fileData = {
                ...response.data,
                key: response.data.fileName || response.data.key,
                name: response.data.originalName || upload.file.name,
                fullUrl: response.data.url,
                url: response.data.url
              };
            } else {
              console.error('❌ Unexpected response structure:', response.data);
              continue;
            }
            
            // In image preview mode, if not multiple, replace any existing files
            if (this.imagePreviewMode && !this.multiple) {
              this.files = [fileData];
            } else {
              this.files.unshift(fileData);
            }
            
            uploadedFiles.push(fileData);
            
            // Clear the preview image as we now have the actual URL
            this.previewImage = null;
          }

          // Remove successful upload from queue
          const queueIndex = this.uploadQueue.findIndex(q => q === upload);
          if (queueIndex !== -1) {
            this.uploadQueue.splice(queueIndex, 1);
          }
          
        } catch (error) {
          console.error('Upload failed:', error);
          upload.uploading = false;
          upload.progress = undefined;
          this.showToastMessage(`Failed to upload ${upload.file.name}: ${error.response?.data?.message || error.message}`, 'error');
        }
      }
      
      this.uploading = false;
      
      // Emit event with uploaded files
      if (uploadedFiles.length > 0) {
        this.showToastMessage('Files uploaded successfully!', 'success');
        this.$emit('files-uploaded', uploadedFiles);
      }
    },

    // File management
    copyUrl(url) {
      if (!url) return;
      
      try {
        navigator.clipboard.writeText(url).then(() => {
          this.showToastMessage('URL copied to clipboard!', 'success');
        });
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.showToastMessage('URL copied to clipboard!', 'success');
        } catch (fallbackErr) {
          console.error('Could not copy text: ', fallbackErr);
          this.showToastMessage('Failed to copy URL to clipboard', 'error');
        }
        document.body.removeChild(textArea);
      }
    },

    previewFile(file) {
      const url = file.url || file.fullUrl;
      if (!url) return;
      
      // Open preview in new tab
      window.open(url, '_blank');
      this.$emit('file-previewed', file);
    },

    async deleteFile(file) {
      if (!confirm(`Are you sure you want to delete "${file.name || file.key}"?`)) {
        return;
      }
      
      try {
        const fileKey = file.key || file.id;
        await axios.delete(`${this.apiUrl}/${encodeURIComponent(fileKey)}`);
        
        // Remove from files list
        const index = this.files.findIndex(f => (f.key || f.id) === fileKey);
        if (index !== -1) {
          this.files.splice(index, 1);
        }
        
        this.showToastMessage('File deleted successfully!', 'success');
        this.$emit('file-deleted', file);
      } catch (error) {
        console.error('Delete failed:', error);
        this.showToastMessage(`Failed to delete file: ${error.response?.data?.message || error.message}`, 'error');
      }
    },

    // Toast functionality
    showToastMessage(message, type = 'success') {
      this.showToast.show = true;
      this.showToast.message = message;
      this.showToast.type = type;
      
      setTimeout(() => {
        this.showToast.show = false;
      }, 3000);
    }
  }
};
</script>

<style scoped>
.file-uploader {
  width: 100%;
}

/* Upload area styling */
.upload-area {
  cursor: pointer;
  transition: all 0.3s ease;
}

.upload-area:hover {
  border-color: #3b82f6;
  background-color: #f0f9ff;
}

/* Progress bar styling */
.upload-item .bg-blue-600 {
  transition: width 0.3s ease;
}

/* Toast positioning */
.fixed.bottom-4.right-4 {
  z-index: 1000;
}

/* Image preview container */
.image-preview-container {
  width: 100%;
}

.image-preview-wrapper {
  max-width: 100%;
  overflow: hidden;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Gallery mode styling */
.gallery-view .aspect-w-1 {
  position: relative;
  padding-bottom: 100%;
}

.gallery-view .aspect-h-1 {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
}

/* Animation */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 